import { useTranslations } from 'js/hooks/i18n';
import { Icon } from 'js/components/common/Icon';
import { clsb, clsx } from 'js/utils/classNames.util';
import { componentService } from 'js/services/component.service';
import React, { useCallback, memo, useRef, useState, useEffect, useMemo } from 'react';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from './helpers';
import { withFinalFormField } from '../../hocs';
import { getInteractionClass } from '../../helpers';
import { FieldBottomMessage } from '../FieldBottomMessage';

import '../../FormFields.scss';
import style from './Select.scss';

export const SelectBare: React.FC<Types.ISelectProps> = ({
    id,
    meta,
    hint,
    label,
    onChange,
    readOnly,
    disabled,
    leftIcon,
    rightIcon,
    className,
    classNames,
    infoTooltip,
    onMouseDown,
    options = [],
    currencySymbol,
    placeholder = '',
    'aria-label': ariaLabel,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    ...props
}) => {
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const translate = useTranslations();
    const selectRef = useRef<HTMLSelectElement>(null);
    const selectOptions = useMemo(() => helpers.parseOptions(options), [options]);
    const interactionClass = getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });
    const propsValue = props.value || props.defaultValue;
    // needed to change placeholder option color
    const emptySelectClassName = props.value !== null && props.value !== '' && props.defaultValue !== '' ? '' : style.empty;

    const preventClick = useCallback((event: React.MouseEvent): void => event.preventDefault(), []);

    // this section is needed to use select as replacer
    // if not needed could be replaced with const selectValue = helpers.getSelectLabel(options, String(props.value), placeholder)
    const [selectValue, setSelectValue] = useState(helpers.getSelectLabel(selectOptions, String(propsValue), placeholder));

    const onSelectChange = useCallback((event: React.FocusEvent<HTMLSelectElement>): void => {
        onChange && onChange(event);

        setSelectValue(helpers.getSelectLabel(selectOptions, String(event.target.value), placeholder));
    }, [onChange, selectOptions, placeholder]);

    useEffect(() => {
        setSelectValue(helpers.getSelectLabel(selectOptions, String(propsValue), placeholder));
    }, [selectOptions, placeholder, propsValue]);
    // end region

    return (
        <div className={clsx([clsb([style, classNames], ['container']), emptySelectClassName, interactionClass, className])}>
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    id={id}
                    label={label}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}

            <div className={clsb([style, classNames], ['innerContainer'])}>
                {leftIcon && leftIcon()}

                {currencySymbol && <span className={clsb([style, classNames], ['currencySymbol'])}>{currencySymbol}</span>}

                <select
                    id={id}
                    ref={selectRef}
                    disabled={isDisabled}
                    onChange={onSelectChange}
                    tabIndex={isDisabled || readOnly ? -1 : 0}
                    className={clsb([style, classNames], ['select'])}
                    onMouseDown={readOnly ? preventClick : onMouseDown}
                    aria-label={ariaLabel || translate('playtech.mobile.system.select.aria-label')}
                    {...props}
                >
                    {(placeholder || isPlaceholderAsteriskEnabled) && (
                        <option
                            value=""
                            disabled
                            className={clsb([style, classNames], ['placeholder'])}
                        >
                            {`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                        </option>
                    )}
                    {selectOptions.map(item => (
                        <option
                            value={item.value}
                            key={item.key || item.value}
                            className={classNames?.option}
                        >
                            {item.label}
                        </option>
                    ))}
                </select>

                <div className={style.selectDummy}>{selectValue}</div>

                {rightIcon && rightIcon()}

                <Icon
                    type="arrowDropdown"
                    disabled={isDisabled || readOnly}
                    className={clsb([style, classNames], ['dropDownIcon'])}
                />
            </div>

            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const SelectPure = memo(SelectBare);
export const Select = withFinalFormField<string>()(SelectPure);

componentService.registerWebContentComponents({ Select: SelectPure });
